import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Async thunks
export const fetchPreOrderProducts = createAsyncThunk(
  'preOrders/fetchPreOrderProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/products', {
        params: { pre_order: true }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch pre-order products');
    }
  }
);

export const fetchMyPreOrders = createAsyncThunk(
  'preOrders/fetchMyPreOrders',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.get('/api/pre-orders', {
        params: { user_id: auth.user.id },
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch pre-orders');
    }
  }
);

export const createPreOrder = createAsyncThunk(
  'preOrders/createPreOrder',
  async ({ productId, quantity, paymentType }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.post('/api/pre-orders',
        {
          user_id: auth.user.id,
          product_id: productId,
          quantity,
          payment_type: paymentType
        },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create pre-order');
    }
  }
);

export const cancelPreOrder = createAsyncThunk(
  'preOrders/cancelPreOrder',
  async (preOrderId, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      await axios.put(`/api/pre-orders/${preOrderId}`,
        { status: 'cancelled' },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return preOrderId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to cancel pre-order');
    }
  }
);

const initialState = {
  products: [],
  myPreOrders: [],
  loading: false,
  submitting: false,
  error: null,
};

const preOrdersSlice = createSlice({
  name: 'preOrders',
  initialState,
  reducers: {
    clearPreOrderError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch pre-order products
      .addCase(fetchPreOrderProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPreOrderProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.products || action.payload;
      })
      .addCase(fetchPreOrderProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch my pre-orders
      .addCase(fetchMyPreOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMyPreOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.myPreOrders = action.payload;
      })
      .addCase(fetchMyPreOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create pre-order
      .addCase(createPreOrder.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createPreOrder.fulfilled, (state, action) => {
        state.submitting = false;
        state.myPreOrders.unshift(action.payload);
      })
      .addCase(createPreOrder.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })
      // Cancel pre-order
      .addCase(cancelPreOrder.fulfilled, (state, action) => {
        const index = state.myPreOrders.findIndex(po => po.id === action.payload);
        if (index !== -1) {
          state.myPreOrders[index].status = 'cancelled';
        }
      });
  },
});

export const { clearPreOrderError } = preOrdersSlice.actions;
export default preOrdersSlice.reducer;
